import { buildCombinationKey } from '../../../../../orders/products/product/grid/store/gridStore.utils';
import { AvailabilityEnum } from '../../../../../products/ProductTypes';
import {
  CatalogGridWindowedAvailability,
  CatalogSectionDeliveryWindow,
} from '../../../interfaces/catalogProductList.types';

export interface CatalogListCombination {
  availability: AvailabilityEnum;
  combinationKey: string;
  deliveryWindow?: CatalogSectionDeliveryWindow;
}

interface BuildCatalogListCombinationsParams {
  availability: AvailabilityEnum[];
  productColorId: string;
  deliveryWindowsByAvailability?: Partial<
    Record<CatalogGridWindowedAvailability, CatalogSectionDeliveryWindow[]>
  >;
  orderId?: string;
}

const WINDOWED_AVAILABILITIES: CatalogGridWindowedAvailability[] = [
  AvailabilityEnum.INFINITE_PRE_ORDER,
  AvailabilityEnum.MATERIAL_PRE_ORDER,
  AvailabilityEnum.FUTURE_STOCK,
];

interface WindowedCombination {
  availability: CatalogGridWindowedAvailability;
  deliveryWindow: CatalogSectionDeliveryWindow;
}

const compareWindowedCombinations = (first: WindowedCombination, second: WindowedCombination) => {
  const startDateComparison = first.deliveryWindow.startDate.localeCompare(
    second.deliveryWindow.startDate,
  );

  if (startDateComparison !== 0) {
    return startDateComparison;
  }

  return (
    WINDOWED_AVAILABILITIES.indexOf(first.availability) -
    WINDOWED_AVAILABILITIES.indexOf(second.availability)
  );
};

const toWindowedCombinations = (
  availability: CatalogGridWindowedAvailability,
  windows: CatalogSectionDeliveryWindow[],
): WindowedCombination[] => windows.map((deliveryWindow) => ({ availability, deliveryWindow }));

export const buildCatalogListCombinations = ({
  availability,
  productColorId,
  deliveryWindowsByAvailability,
  orderId,
}: BuildCatalogListCombinationsParams): CatalogListCombination[] => {
  const windowed = WINDOWED_AVAILABILITIES.filter((windowedAvailability) =>
    availability.includes(windowedAvailability),
  ).flatMap((windowedAvailability) =>
    toWindowedCombinations(
      windowedAvailability,
      deliveryWindowsByAvailability?.[windowedAvailability] ?? [],
    ),
  );

  const ordered: Omit<CatalogListCombination, 'combinationKey'>[] = [];

  if (availability.includes(AvailabilityEnum.READY_FOR_DELIVERY)) {
    ordered.push({ availability: AvailabilityEnum.READY_FOR_DELIVERY });
  }

  ordered.push(...windowed.sort(compareWindowedCombinations));

  return ordered.map((combination) => ({
    ...combination,
    combinationKey: buildCombinationKey({
      productColorId,
      availability: combination.availability,
      deliveryWindowId: combination.deliveryWindow?.id,
      orderId,
    }),
  }));
};
